import RegionModel from "../models/regionModel";
import sortBy from "lodash/sortBy";
import { cropr, gettext } from "../adapter";
import ForecastModel from "../models/forecastModel";
import each from "lodash/each";
import isEmpty from "lodash/isEmpty";
import min from "lodash/min";
import max from "lodash/max";
import simplify from "simplify-js";
import ForecastDayPartModel from "../models/forecastDayPartModel";
import ForecastDayModel from "../models/forecastDayModel";
import { saveAs } from "./globalUtils";
import moment from "moment";

export const calcDewPoint = (rh, t) => {
  var dew = 0.0;
  if (t !== 0.0 && rh !== 0.0) {
    rh = rh * 10.0;
    dew = -1 / ((Math.log(rh) - 6.91) / (5411.54 - 5.1642 * t) - 1 / (t + 273.16)) - 273.16;
  }
  return dew;
};

const simplifyFactor = 0.75;

const compressContinuous = (data, scale, brush) => {
  var multiplier = 1.5;
  if (brush) multiplier = 2;
  var simplified = simplify(data, simplifyFactor * multiplier, true); // 1

  // Remove the scale
  return (simplified = simplified.map(function (measurement) {
    return { timestamp: measurement.timestamp, y: measurement.y / scale };
  }));
};

export const parseRegions = (items) => {
  var regions = items;

  regions = regions.map((region) => {
    var r = new RegionModel(
      region.xbaseid,
      region.name,
      region.latitude,
      region.longitude,
      region.country,
      region.id,
      region.distance,
      -region.utc_offset_minutes // inverse offset cause js works with inverse offsets see: https://www.w3schools.com/jsref/jsref_gettimezoneoffset.asp
    );

    return r;
  });

  return sortBy(regions, "name");
};

export const parseRegion = (data) => {
  var fcast = data;
  var days = fcast.summaryforecast;
  var days24 = fcast.dailyforecast;
  var sprayplanner = fcast.sprayplanner;
  var region = new RegionModel(
    fcast.xbaseid,
    fcast.name,
    fcast.latitude,
    fcast.longitude,
    fcast.country,
    fcast.id,
    fcast.distance,
    -fcast.utc_offset_minutes
  );

  days = days.map(function (day) {
    var forecasts = day.forecasts.reduce(function (result, forecast) {
      if (forecast) {
        result.push(
          new ForecastDayPartModel({
            hour: forecast.hour,
            descriptor: forecast.descriptor,
            maximumTemperature: cropr.l10n_utils.celsius_to_l10n(forecast.maximum_temperature),
            minimumHumidity: forecast.minimum_humidity,
            minimumTemperature: cropr.l10n_utils.celsius_to_l10n(forecast.minimum_temperature),
            precipitationAmount: cropr.l10n_utils.mm_to_l10n(forecast.precipitation_amount),
            precipitationChance: forecast.precipitation_chance,
            predictedEvapotranspiration: forecast.predicted_evapotranspiration,
            windDirection: forecast.wind_direction,
            windSpeed: cropr.l10n_utils.mps_to_l10n(forecast.wind_speed),
          })
        );
      }
      return result;
    }, []);

    return new ForecastDayModel({
      date: day.date,
      predictedEvapotranspiration: day.predicted_evapotranspiration,
      forecasts: forecasts,
    });
  });

  days24 = days24.map(function (day) {
    var forecasts = day.forecasts.reduce((result, forecast) => {
      if (forecast) {
        result.push(
          new ForecastDayPartModel({
            hour: forecast.hour,
            descriptor: forecast.descriptor,
            maximumTemperature: cropr.toLocaleFixed(
              cropr.l10n_utils.celsius_to_l10n(forecast.maximum_temperature),
              1
            ),
            minimumHumidity: forecast.minimum_humidity,
            minimumTemperature: cropr.toLocaleFixed(
              cropr.l10n_utils.celsius_to_l10n(forecast.minimum_temperature),
              1
            ),
            precipitationAmount: cropr.l10n_utils.mm_to_l10n(forecast.precipitation_amount),
            precipitationChance: forecast.precipitation_chance,
            predictedEvapotranspiration: forecast.predicted_evapotranspiration,
            windDirection: forecast.wind_direction,
            windSpeed: cropr.l10n_utils.mps_to_l10n(forecast.wind_speed),
          })
        );
      }
      return result;
    }, []);

    return new ForecastDayModel({
      date: day.date,
      predictedEvapotranspiration: day.predicted_evapotranspiration,
      forecasts: forecasts,
    });
  });

  return { region, days, days24, sprayplanner };
};

export const parseRegionData = (data) => {
  if (!data) return data;

  var station = data;

  var forecastModel = new ForecastModel({
    id: station.id,
    //      region: station.region,
    name: station.name,
    latitude: station.latitude,
    longitude: station.longitude,
    enabled: true,
    active: true,
    activePeriodStart: Date.now(),
    activePeriodEnd: Date.now(),
    serialNumber: station.xbaseid,
    xbaseid: station.xbaseid,
  });

  // Calculate the timezone offset
  var d = new Date();
  var timezoneOffset = d.getTimezoneOffset() * 1000 * 60;

  // Values we will measure
  var airTemperature = [];
  var relativeHumidity = [];
  var radiation = [];
  var windSpeed = [];
  var precipitation = [];
  var windDirection = [];
  var precipChance = [];
  var dewPoint = [];

  var allAirTemperatureValues = [];
  var allRelativeHumidityValues = [];
  var allWindSpeedValues = [];
  var allPrecipChanceValues = [];
  var allDewPointValues = [];

  each(station.forecast, function (measurement, key) {
    allAirTemperatureValues.push(measurement.values.air_temperature);
    allRelativeHumidityValues.push(measurement.values.rel_humidity);
    allWindSpeedValues.push(measurement.values.wind_speed);
    allPrecipChanceValues.push(measurement.values.precip_chance);
    allDewPointValues.push(
      calcDewPoint(measurement.values.rel_humidity, measurement.values.air_temperature)
    );
  });

  // save the raw measurements
  forecastModel.forecast.raw = station.forecast;

  var maxAirTemperature = max(allAirTemperatureValues);
  var minAirTemperature = min(allAirTemperatureValues);

  var maxDewPoint = max(allDewPointValues);
  var minDewPoint = min(allDewPointValues);

  var maxRelativeHumidity = max(allRelativeHumidityValues);
  var minRelativeHumidity = min(allRelativeHumidityValues);

  var maxWindSpeed = max(allWindSpeedValues);
  var minWindSpeed = min(allWindSpeedValues);

  var airTemperatureScale = 100 / (maxAirTemperature - minAirTemperature);
  var dewPointScale = 100 / (maxDewPoint - minDewPoint);
  var relativeHumidityScale = 100 / (maxRelativeHumidity - minRelativeHumidity);
  var windSpeedScale = 100 / (maxWindSpeed - minWindSpeed);
  var precipChanceScale = 1; // 100 / (maxPrecipChance - minPrecipChance);

  each(station.forecast, function (measurement, key) {
    var timestamp = measurement.timestamp * 1000 + timezoneOffset;
    var date = new Date(parseInt(timestamp));

    // Add rain to the measurements
    precipitation.push({
      timestamp: date,
      y: measurement.values.precipitation ? measurement.values.precipitation : 0,
    });

    // @TODO: See if we need the speed
    windDirection.push({
      timestamp: date,
      y: measurement.values.wind_speed,
      direction: measurement.values.wind_direction,
    });

    airTemperature.push({
      x: timestamp,
      y: measurement.values.air_temperature * airTemperatureScale,
      timestamp: date,
    });

    dewPoint.push({
      x: timestamp,
      y:
        calcDewPoint(measurement.values.rel_humidity, measurement.values.air_temperature) *
        dewPointScale,
      timestamp: date,
    });

    windSpeed.push({
      x: timestamp,
      y: measurement.values.wind_speed * windSpeedScale,
      timestamp: date,
    });

    relativeHumidity.push({
      x: timestamp,
      y: measurement.values.rel_humidity * relativeHumidityScale,
      timestamp: date,
    });

    radiation.push({
      timestamp: date,
      y: measurement.values.radiation ? measurement.values.radiation : 0,
    });

    precipChance.push({
      timestamp: date,
      x: timestamp,
      y: measurement.values.precip_chance * precipChanceScale,
    });
  });

  airTemperature = compressContinuous(airTemperature, airTemperatureScale, false);
  relativeHumidity = compressContinuous(relativeHumidity, relativeHumidityScale, false);
  precipChance = compressContinuous(precipChance, precipChanceScale, false);
  windSpeed = compressContinuous(windSpeed, windSpeedScale, false);
  dewPoint = compressContinuous(dewPoint, dewPointScale, false);

  forecastModel.measurements.airTemperature = airTemperature;
  forecastModel.forecast.airTemperature = airTemperature;
  forecastModel.forecast.precipitation = precipitation;
  forecastModel.forecast.precipChance = precipChance;
  forecastModel.measurements.precipitation = precipitation;
  forecastModel.measurements.windSpeed = windSpeed;
  forecastModel.measurements.relativeHumidity = relativeHumidity;
  forecastModel.measurements.radiation = radiation;
  forecastModel.measurements.windDirection = windDirection;
  forecastModel.measurements.dewPoint = dewPoint;

  // The last data point we received
  var ldp = false;
  if (!isEmpty(forecastModel.forecast.airTemperature)) {
    ldp = forecastModel.forecast.airTemperature[forecastModel.forecast.airTemperature.length - 1];
  }

  var firstDataPointTimestamp = false;

  if (station.forecast[0]) {
    firstDataPointTimestamp = station.forecast[0].timestamp;
  }

  var firstDataPoint;
  if (ldp && firstDataPointTimestamp) {
    firstDataPoint = new Date(parseInt(firstDataPointTimestamp * 1000 + timezoneOffset));
  }

  forecastModel.firstDataPointDate = firstDataPoint;
  forecastModel.lastDataPointDate = ldp.timestamp;

  return { region: forecastModel };
};

export const export2Csv = (region) => {
  var valuescsv = "date\ttime";
  var offset = 0; //d.getTimezoneOffset()*1000*60;

  var units = {
    air_temperature: cropr.l10n.unit_temp_display,
    dewpoint: cropr.l10n.unit_temp_display,
    precip_chance: "%",
    precipitation: cropr.l10n.unit_precipitation_display,
    radiation: gettext("J/cm\u00b2"),
    rel_humidity: "%",
    wind_direction: "\u00b0",
    wind_speed: cropr.l10n.unit_windspeed_display,
    cloud: "unit 1-8",
  };

  //output the table header
  each(region.forecast.raw[0].values, function (measurement, key) {
    //do not include depths
    if (key !== "depths") {
      valuescsv += "\t" + key;
    }
  });
  valuescsv += "\tdewpoint\r\n\t";

  each(region.forecast.raw[0].values, function (measurement, key) {
    //do not include depths
    if (key !== "depths") {
      valuescsv += "\t" + units[key];
    }
  });
  valuescsv += "\t" + cropr.l10n.unit_temp_display;

  //output the actual measurement values
  each(region.forecast.raw, function (measurement, key) {
    valuescsv += "\r\n";
    var ts = new Date(measurement.timestamp * 1000 + offset);

    valuescsv +=
      moment(ts)
        .format("L")
        .replace(/\u200e/g, "") + "\t"; // the unicode 200e sign is an IE bug, so remove those characters.
    valuescsv += moment(ts)
      .format("LT")
      .replace(/\u200e/g, "");

    each(measurement.values, function (mval, keyinner) {
      // only include finite numbers
      if (Number.isFinite(mval)) {
        switch (keyinner) {
          case "precipitation":
            mval = cropr.l10n_utils.mm_to_l10n(mval);
            break;
          case "air_temperature":
            mval = cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(mval), 2);
            break;
          case "wind_speed":
            mval = cropr.toLocaleFixed(cropr.l10n_utils.mps_to_l10n(mval), 2);
            break;
          default:
            mval = cropr.toLocaleFixed(mval, 2);
            break;
        }

        valuescsv += "\t" + mval;
      } else {
        //do not include depths
        if (keyinner !== "depths") {
          valuescsv += "\t-";
        }
      }
    });
    valuescsv +=
      "\t" +
      cropr.l10n_utils
        .celsius_to_l10n(
          calcDewPoint(measurement.values.rel_humidity, measurement.values.air_temperature)
        )
        .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });
  saveAs(valuescsv, "dacom_weather_forecast.csv");
};
